import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatFormField } from '@angular/material/form-field';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle, MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatCell, MatHeaderCell, MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faComment, faInfoCircle, faPen, faPlane, faPlusCircle, faSearch, faTrash } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Observable } from 'rxjs';
import { FileResponse } from 'src/app/shared/models/autogenerated-piping';
import { productionGuard } from 'src/app/core/guards/guards';
import { MatPaginatorGoToComponent } from 'src/app/shared/components/mat-paginator-go-to/mat-paginator-go-to.component';
import { SharedModule } from '../../shared.module';


@Component({
    selector: 'app-dictionary-simple-page',
    templateUrl: './dictionary-simple-page.component.html',
    imports: [
        CommonModule,
        FontAwesomeModule,
        MatCell,
        MatFormField,
        MatHeaderCell,
        MatProgressBarModule,
        MatSelectModule,
        MatSlideToggle,
        MatSlideToggleModule,
        MatTable,
        MatTableModule,
        SharedModule,
    ],
    styleUrls: ['./dictionary-simple-page.component.scss']
})
export abstract class DictionarySimplePageComponent<T> implements OnInit {
  static readonly SEARCH_QUERY_NAME = 'searchQuery';
  static readonly IS_ACTIVE_NAME = 'isActive';
  @Input() groupData: string[] = [];
  @Input() typeData: string[] = [];
  dictionaryDefinition: DictionaryItemDefinition<T>;
  currentPage = 0;
  totalCount: number;
  lastPage: number;
  pageSize = 100;
  pageSizes = [2, 5, 10, 25, 50, 100];
  filterIsShowAll = false;
  showClearSearchButton = true;
  groupFilterPlaceholder: string = 'Sort by group';
  groupFilterAriaLabel: string = 'Select a Group';
  typeFilterPlaceholder: string = 'Sort by types';
  typeFilterAriaLabel: string = 'Select a Type';

  staticColumnNames: string[] = [
    'actions'
  ];
  columnNames: string[];

  faEdit = faPen;
  faSearch = faSearch;
  faInfo = faInfoCircle;
  faCreate = faPlusCircle;
  faTrashCan = faTrash;
  faPlane = faPlane;
  faComment = faComment;

  /**
   * Optional export function. When provided, an "Export to Excel" button
   * is rendered in the header row of the shared template.
   * Child classes set this in their constructor or `childOnInitBefore` to wire up
   * the appropriate BE export endpoint via `DictionaryExportService`.
   */
  exportFn: (() => Observable<FileResponse>) | null = null;

  filterDictionary = new Map<string, string>();

  searching$ = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<T>();

  @ViewChild('searchInput') searchInput: ElementRef;

  @ViewChild('paginator') set paginator(paginator: MatPaginatorGoToComponent) {
    this.dataSource.paginator = paginator.paginator;
  }

  @ViewChild(MatSort, { static: false }) set sorter(sort: MatSort) {
    if (this.dataSource) {
      this.dataSource.sortingDataAccessor = (item, property) => {
        const dictionaryItem = this.dictionaryDefinition.dictionaryItems.find(di => di.propertyName === property);
        if (dictionaryItem) {
          return this.normalizeToSmallCaps(dictionaryItem.getItem(item));
        } else {
          return this.normalizeToSmallCaps(item[property].toString());
        }
      };
      this.dataSource.sort = sort;
    }
  }

  ngOnInit(): void {
    this.childOnInitBefore();

    this.dictionaryDefinition = this.initializeDictionaryDefinition();
    this.columnNames = this.dictionaryDefinition.dictionaryItems.map(item => item.propertyName);
    if (this.dictionaryDefinition.displayStatusColumn) {
      this.columnNames.push("isActive");
    }
    if (productionGuard() || this.dictionaryDefinition.displayDeleteButton) {
      this.columnNames = this.columnNames.concat(this.staticColumnNames);
    }
    this.searchAll();
    this.filterDictionary.set(DictionarySimplePageComponent.SEARCH_QUERY_NAME, '');
    this.dataSource.filterPredicate = (record, filter) => {
      const map = new Map<string, string>(JSON.parse(filter));
      return this.getFilterPredicate(record, map);
    };
    this.search();

    this.childOnInitAfter();
  }

  public updatePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }

  setSearchValueAndSearch(event) {
    this.filterDictionary.set(DictionarySimplePageComponent.SEARCH_QUERY_NAME, event.target.value.trim().toLocaleLowerCase());
    if (event.keyCode == 13) {
      this.search();
    }
  }

  public search() {
    // Reset to page 1 so filtered results always start from the beginning
    this.currentPage = 0;
    if (this.filterIsShowAll) {
      this.filterDictionary.delete(DictionarySimplePageComponent.IS_ACTIVE_NAME);
    } else {
      this.filterDictionary.set(DictionarySimplePageComponent.IS_ACTIVE_NAME, true.toString());
    }
    const newFilter: string = JSON.stringify(Array.from(this.filterDictionary.entries()));
    // MatTableDataSource only re-evaluates the predicate when the filter string changes.
    // When the filter criteria haven't changed (e.g. after a row status toggle), force
    // re-evaluation by briefly clearing the filter before assigning the new value.
    if (this.dataSource.filter === newFilter) {
      this.dataSource.filter = '';
    }
    this.dataSource.filter = newFilter;
  }

  public clearSearchText() {
    this.currentPage = 0;
    this.filterDictionary.set(DictionarySimplePageComponent.SEARCH_QUERY_NAME, '');
    this.searchInput.nativeElement.value = '';
    this.search();
  }

  toggle(event: MatSlideToggleChange) {
    this.filterIsShowAll = event.checked;
    this.search();
  }

  protected normalizeToSmallCaps(sortString: string): string {
    let cleanString = '';
    if (sortString) {
      cleanString = sortString.trim();
    }
    return cleanString.toLowerCase();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<T> {
    return new DictionaryItemDefinition();
  }

  protected abstract childOnInitBefore(): void; // Not ideal. To be implemented in child classes
  protected abstract childOnInitAfter(): void; // Not ideal. To be implemented in child classes

  protected abstract updateStatus(item: T): void;

  protected abstract searchAll(): void;

  protected abstract getFilterPredicate(record: T, parametersMap: Map<string, string>): boolean;

  protected abstract createNew(): void;

  protected abstract editItem(item: T): void;

  protected abstract editItemReferenced(item: object): void;

  protected abstract deleteItem(item: T): void;

  protected abstract getUsageInformation(item: object): void;

  protected abstract getNoMatchMessage(): string;

  protected selectionChange(event: any, type: string): void {
  }

  protected clearAll(): void { }

  protected canDeleteItem(item: T): boolean {
    const hasAssociations = (item as any)['hasAssociations'];
    return !hasAssociations;
  }

}


export class DictionaryItem<T> {
  displayName: string;
  propertyName: string;
  // standard or extended
  columnSize = 'standard';
  /** Optional badge label rendered next to the cell value, e.g. 'Always available'. Returns null/undefined to render no badge. */
  getBadge?: (item: T) => string | null | undefined;

  constructor(displayName: string, propertyName: string, columnSize?: string, getItemFunction?, getBadgeFunction?: (item: T) => string | null | undefined) {
    this.displayName = displayName;
    this.propertyName = propertyName;
    if (getItemFunction) {
      this.getItem = getItemFunction;
    }
    if (getBadgeFunction) {
      this.getBadge = getBadgeFunction;
    }

    if (columnSize) {
      this.columnSize = columnSize;
    }
  }

  getItem = (item: T) => {
    return item[this.propertyName];
  }
}

export class DictionaryItemDefinition<T> {
  displayStatusColumn = false;
  displayDeleteButton? = true;
  dictionaryItems: DictionaryItem<T>[];
}
